import React, { useMemo, useState } from 'react';
import { Button, Search } from '@carbon/react';
import { ChevronLeft, ChevronRight } from '@carbon/icons-react';
import { useDnD } from '../context/DnDContext';
import { nodeCategories } from '../constants/nodeCategories';
import { getNodeDragPayload, enrichNodeConfig } from '../utils/nodeMeta';

const DndNodeCard = ({ node, category, onDragStart }) => (
  <div
    className="dnd-node"
    style={{ '--node-accent': category.color }}
    draggable
    onDragStart={(e) => onDragStart(e, node, category)}
    data-testid={`dnd-node-${node.nodeType}`}
    role="button"
    tabIndex={0}
    aria-label={`Drag ${node.label} to canvas`}
  >
    <div className="dnd-node-tile">
      <div className="dnd-node-accent" aria-hidden />
      <div className="dnd-node-content">
        <div className="dnd-node-icon-wrap">{node.icon}</div>
        <div className="dnd-node-info">
          <span className="dnd-node-label">{node.label}</span>
          <span className="dnd-node-subtitle">{node.subtitle}</span>
        </div>
      </div>
    </div>
  </div>
);

export const Sidebar = ({ collapsed, onToggle }) => {
  const [, setType, , setNodeData] = useDnD();
  const [searchTerm, setSearchTerm] = useState('');

  const onDragStart = (event, node, category) => {
    const payload = getNodeDragPayload(node, category);
    setType(node.type);
    setNodeData(enrichNodeConfig(payload));
    event.dataTransfer.setData('application/etl-node', JSON.stringify(payload));
    event.dataTransfer.effectAllowed = 'move';
  };

  const filteredCategories = useMemo(() => {
    const withoutGatewayDrag = nodeCategories
      .map((category) => ({
        ...category,
        nodes: category.nodes.filter((node) => node.nodeType !== 'kGateway'),
      }))
      .filter((category) => category.nodes.length > 0);

    const term = searchTerm.trim().toLowerCase();
    if (!term) return withoutGatewayDrag;

    return withoutGatewayDrag
      .map((category) => ({
        ...category,
        nodes: category.nodes.filter(
          (node) =>
            node.label.toLowerCase().includes(term) ||
            node.subtitle.toLowerCase().includes(term) ||
            node.nodeType.toLowerCase().includes(term)
        ),
      }))
      .filter((category) => category.nodes.length > 0);
  }, [searchTerm]);

  const collapsedCategories = useMemo(
    () => nodeCategories
      .map((category) => ({
        ...category,
        nodes: category.nodes.filter((node) => node.nodeType !== 'kGateway'),
      }))
      .filter((category) => category.nodes.length > 0),
    [],
  );

  if (collapsed) {
    return (
      <aside className="sidebar collapsed" data-testid="sidebar-collapsed">
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={ChevronRight}
          iconDescription="Expand node library"
          onClick={onToggle}
          className="sidebar-toggle-btn"
        />
        <div className="sidebar-collapsed-content">
          {collapsedCategories.map((category) => (
            <div key={category.id} className="sidebar-collapsed-group">
              {category.nodes.map((node) => (
                <div
                  key={`${node.nodeType}-${node.label}`}
                  className="dnd-node dnd-node--collapsed"
                  style={{ '--node-accent': category.color }}
                  draggable
                  onDragStart={(e) => onDragStart(e, node, category)}
                  title={`${node.label} — ${node.subtitle}`}
                  data-testid={`dnd-node-collapsed-${node.nodeType}`}
                  role="button"
                  tabIndex={0}
                  aria-label={`Drag ${node.label} to canvas`}
                >
                  <div className="dnd-node-icon-wrap dnd-node-icon-wrap--collapsed">
                    {node.icon}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </aside>
    );
  }

  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-header-text">
          <span className="sidebar-title">Node library</span>
          <span className="sidebar-subtitle">Drag onto the canvas</span>
        </div>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={ChevronLeft}
          iconDescription="Collapse node library"
          onClick={onToggle}
          className="sidebar-toggle-btn"
        />
      </div>

      <div className="sidebar-search">
        <Search
          size="sm"
          labelText="Search nodes"
          placeholder="Search nodes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="sidebar-search-nodes"
        />
      </div>

      <div className="sidebar-content">
        {filteredCategories.length === 0 ? (
          <p className="sidebar-empty">No nodes match your search.</p>
        ) : (
          filteredCategories.map((category) => (
            <div key={category.id} className="node-category">
              <div className="category-header" style={{ '--node-accent': category.color }}>
                <span className="category-accent" aria-hidden />
                <span className="category-name">{category.name}</span>
                <span className="category-count">{category.nodes.length}</span>
              </div>

              <div className="category-nodes">
                {category.nodes.map((node) => (
                  <DndNodeCard
                    key={`${node.nodeType}-${node.label}`}
                    node={node}
                    category={category}
                    onDragStart={onDragStart}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </aside>
  );
};
